import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Paper,
    Typography,
    Button,
    useTheme,
    Box,
    Alert
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StopIcon from '@mui/icons-material/Stop';
import { styled, alpha } from '@mui/material/styles';

const CameraContainer = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'variant',
})(({ theme, isActive, variant }) => {
    const isFull = variant === 'full';

    return {
        width: '100%',
        minHeight: isFull ? theme.spacing(30) : theme.spacing(12),
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        padding: isFull ? theme.spacing(4) : theme.spacing(2),
        border: `2px dashed ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        background: isActive
            ? alpha(theme.palette.primary.main, 0.05)
            : theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.grey[50],
        transition: 'all 0.25s ease',
        boxShadow: isFull ? theme.shadows[2] : 'none',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: isFull ? theme.shadows[4] : 'none',
        },
        '& video': {
            width: '100%',
            height: 'auto',
            borderRadius: theme.spacing(1),
            maxHeight: isFull ? '500px' : '200px',
        },
        '& canvas': {
            display: 'none',
        },
    };
});

const Overlay = styled('div')(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    height: '40%',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
    pointerEvents: 'none',
    zIndex: 1,
}));

/**
 * Reusable Camera Input Component
 * 
 * Provides camera access with start/stop functionality.
 * Similar API to FileDropZone for consistency.
 * 
 * @param {Object} props
 * @param {'full'|'small'} props.variant - Camera input variant
 * @param {Function} props.onStart - Callback when camera starts (receives stream)
 * @param {Function} props.onStop - Callback when camera stops
 * @param {Function} props.onError - Callback when error occurs (receives error message)
 * @param {Function} props.onScan - Optional callback for scanning results (for barcode/QR scanners)
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle/description text
 * @param {string} props.startButtonText - Start button text
 * @param {string} props.stopButtonText - Stop button text
 * @param {ReactNode} props.icon - Custom icon
 * @param {Object} props.sx - Additional MUI sx styles
 * @param {boolean} props.disabled - Disable the camera input
 * @param {Object} props.constraints - MediaStreamConstraints for camera (default: { facingMode: 'environment' })
 * @param {boolean} props.showOverlay - Show scanning overlay (default: true)
 * @param {string} props.containerId - ID for the container element (for external scanning libraries)
 * @param {boolean} props.showError - Show error alert internally (default: true). Set to false if parent handles errors
 */
export const CameraInput = ({
    variant = 'full',
    onStart,
    onStop,
    onError,
    onScan,
    title,
    subtitle,
    startButtonText,
    stopButtonText,
    icon,
    sx,
    disabled = false,
    constraints = { video: { facingMode: 'environment' } },
    showOverlay = true,
    showError = true,
    containerId = 'camera-container',
    ...otherProps
}) => {
    const theme = useTheme();
    const [isActive, setIsActive] = useState(false);
    const [error, setError] = useState(null);
    const [stream, setStream] = useState(null);
    const containerRef = useRef(null);
    const videoRef = useRef(null);

    const isFull = variant === 'full';

    // Default content
    const defaultTitle = isActive
        ? 'Camera is active'
        : 'Click to start camera';

    const defaultSubtitle = isActive
        ? 'Point camera at target'
        : 'Allow camera access when prompted';

    const defaultStartButtonText = 'Start Camera';
    const defaultStopButtonText = 'Stop Camera';

    const displayTitle = title || defaultTitle;
    const displaySubtitle = subtitle || defaultSubtitle;
    const displayStartButtonText = startButtonText || defaultStartButtonText;
    const displayStopButtonText = stopButtonText || defaultStopButtonText;
    const displayIcon = icon || <CameraAltIcon sx={{ fontSize: isFull ? '4rem' : 'default', color: theme.palette.primary.main }} />;

    const stopCamera = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setIsActive(false);
        setError(null);

        if (onStop) {
            onStop();
        }
    }, [stream, onStop]);

    const startCamera = useCallback(async () => {
        if (disabled || isActive) return;

        setError(null);

        // Check browser support - handle both modern and legacy APIs
        let getUserMedia;
        let enumerateDevices;

        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            // Modern API
            getUserMedia = (constraints) => navigator.mediaDevices.getUserMedia(constraints);
            enumerateDevices = () => navigator.mediaDevices.enumerateDevices();
        } else if (navigator.getUserMedia) {
            // Legacy API (for older browsers)
            getUserMedia = (constraints) => {
                return new Promise((resolve, reject) => {
                    navigator.getUserMedia(constraints, resolve, reject);
                });
            };
            enumerateDevices = null; // Not available in legacy API
        } else {
            // Check if HTTPS is required (mobile browsers need HTTPS except localhost)
            const isSecureContext = window.isSecureContext ||
                window.location.protocol === 'https:' ||
                window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1';

            if (!isSecureContext) {
                const hostname = window.location.hostname;
                const isLocalIP = /^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[01])\./.test(hostname);

                // For testing: Show warning but still try to access camera
                // Mobile browsers may still block it, but we'll attempt anyway
                if (isLocalIP) {
                    console.warn('Camera access on local IP may require HTTPS. Attempting anyway for testing...');
                }

                // Try to use legacy API if available
                if (navigator.getUserMedia) {
                    getUserMedia = (constraints) => {
                        return new Promise((resolve, reject) => {
                            navigator.getUserMedia(constraints, resolve, reject);
                        });
                    };
                } else {
                    const errorMsg = 'Camera access is not supported in this browser. Please use a modern browser with camera support.';
                    setError(errorMsg);
                    if (onError) {
                        onError(errorMsg);
                    }
                    return;
                }
            } else {
                const errorMsg = 'Camera access is not supported in this browser. Please use a modern browser with camera support.';
                setError(errorMsg);
                if (onError) {
                    onError(errorMsg);
                }
                return;
            }
        }

        // Check permission state if Permissions API is available
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const permissionStatus = await navigator.permissions.query({ name: 'camera' });
                if (permissionStatus.state === 'denied') {
                    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                    let errorMsg = 'Camera permission is blocked. ';
                    if (isMobile) {
                        errorMsg += 'On mobile: Go to browser settings → Site settings → Camera → Allow. Then refresh the page.';
                    } else {
                        errorMsg += 'Click the camera icon in your browser\'s address bar and allow camera access, then refresh the page.';
                    }
                    setError(errorMsg);
                    if (onError) {
                        onError(errorMsg);
                    }
                    return;
                }
            } catch (permErr) {
                // Permissions API might not support 'camera' name in all browsers
                // Continue with normal flow
                console.warn('Could not check camera permission:', permErr);
            }
        }

        // Check for camera devices before requesting access (if enumerateDevices is available)
        let hasCamera = true;
        if (enumerateDevices) {
            try {
                const devices = await enumerateDevices();
                const videoDevices = devices.filter(device => device.kind === 'videoinput');
                hasCamera = videoDevices.length > 0;
                
                if (!hasCamera) {
                    const errorMsg = 'No camera found on this device. Please connect a camera or use the upload image option instead.';
                    setError(errorMsg);
                    if (onError) {
                        onError(errorMsg);
                    }
                    return;
                }
            } catch (enumErr) {
                // If enumeration fails (e.g., permissions not granted yet), continue anyway
                // We'll get a proper error from getUserMedia
                console.warn('Could not enumerate devices:', enumErr);
            }
        }

        try {
            // Request camera permissions
            const mediaStream = await getUserMedia(constraints);
            setStream(mediaStream);
            setIsActive(true);

            // Attach stream to video element
            if (videoRef.current) {
                // Handle both modern and legacy stream assignment
                if ('srcObject' in videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                } else {
                    // Legacy fallback
                    videoRef.current.src = window.URL.createObjectURL(mediaStream);
                }
            }

            // Call onStart callback with stream
            if (onStart) {
                onStart(mediaStream, containerRef.current);
            }
        } catch (err) {
            console.error('Camera error:', err);
            let errorMsg = 'Failed to access camera. ';
            let actionableSteps = '';

            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                errorMsg = 'Camera permission denied. ';
                // Detect mobile vs desktop for better guidance
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                if (isMobile) {
                    actionableSteps = 'On mobile: Go to browser settings → Site settings → Camera → Allow. Then refresh the page.';
                } else {
                    actionableSteps = 'Click the camera icon in your browser\'s address bar and allow camera access, then try again.';
                }
                errorMsg += actionableSteps;
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                // Check if we can enumerate devices to confirm no camera exists
                if (enumerateDevices) {
                    try {
                        const devices = await enumerateDevices();
                        const videoDevices = devices.filter(device => device.kind === 'videoinput');
                        if (videoDevices.length === 0) {
                            errorMsg = 'No camera found on this device. Please connect a camera or use the upload image option instead.';
                        } else {
                            // Camera exists but getUserMedia failed - might be permission issue
                            errorMsg = 'Camera access failed. This might be a permission issue. ';
                            actionableSteps = 'Please check your browser settings and allow camera access, then try again.';
                            errorMsg += actionableSteps;
                        }
                    } catch (enumErr) {
                        errorMsg = 'No camera found or camera access denied. Please check your device has a camera and permissions are granted.';
                    }
                } else {
                    errorMsg = 'No camera found. Please use a device with a camera or try the upload image option instead.';
                }
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                errorMsg = 'Camera is already in use by another application. Please close other apps using the camera and try again.';
            } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
                errorMsg = 'Camera constraints not supported. Trying with default settings...';
                // Try with default constraints
                try {
                    const defaultStream = await getUserMedia({ video: true });
                    setStream(defaultStream);
                    setIsActive(true);
                    if (videoRef.current) {
                        if ('srcObject' in videoRef.current) {
                            videoRef.current.srcObject = defaultStream;
                        } else {
                            videoRef.current.src = window.URL.createObjectURL(defaultStream);
                        }
                    }
                    if (onStart) {
                        onStart(defaultStream, containerRef.current);
                    }
                    return;
                } catch (retryErr) {
                    if (retryErr.name === 'NotAllowedError' || retryErr.name === 'PermissionDeniedError') {
                        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                        if (isMobile) {
                            errorMsg = 'Camera permission denied. Go to browser settings → Site settings → Camera → Allow. Then refresh the page.';
                        } else {
                            errorMsg = 'Camera permission denied. Click the camera icon in your browser\'s address bar and allow camera access.';
                        }
                    } else {
                        errorMsg += retryErr.message || 'Please check camera permissions.';
                    }
                }
            } else {
                errorMsg += err.message || 'Please check camera permissions and try again.';
            }

            setError(errorMsg);
            if (onError) {
                onError(errorMsg);
            }
        }
    }, [disabled, isActive, constraints, onStart, onError]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, [stopCamera]);

    return (
        <Box sx={{ width: '100%', ...sx }} {...otherProps}>
            <CameraContainer
                variant={variant}
                isActive={isActive}
                elevation={isFull ? 2 : 0}
                ref={containerRef}
                id={containerId}
            >
                {isActive ? (
                    <>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                        />
                        {showOverlay && <Overlay />}
                        <Button
                            variant="outlined"
                            color="error"
                            size={isFull ? 'large' : 'medium'}
                            startIcon={<StopIcon />}
                            onClick={stopCamera}
                            disabled={disabled}
                            sx={{ mt: 2 }}
                        >
                            {displayStopButtonText}
                        </Button>
                        {displaySubtitle && (
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                {displaySubtitle}
                            </Typography>
                        )}
                    </>
                ) : (
                    <>
                        {displayIcon}
                        <Typography variant={isFull ? 'h6' : 'body1'} fontWeight={500}>
                            {displayTitle}
                        </Typography>
                        {displaySubtitle && (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                {displaySubtitle}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            size={isFull ? 'large' : 'medium'}
                            startIcon={<CameraAltIcon />}
                            onClick={startCamera}
                            disabled={disabled}
                            sx={{ mt: isFull ? 2 : 1 }}
                        >
                            {displayStartButtonText}
                        </Button>
                    </>
                )}
            </CameraContainer>

            {error && showError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}
        </Box>
    );
};

